import React, { useState, useEffect } from 'react';
import { MapPin, Star, Clock, TrendingUp, Navigation, Car } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

const HotspotRecommendations = () => {
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    fetchHotspots();
    getCurrentLocation();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchHotspots, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchHotspots = async () => {
    try {
      const response = await axiosInstance.get('/driver/hotspots');
      if (response.data.success) {
        // Process hotspots with real location and demand data
        const processedHotspots = response.data.data.hotspots.map(hotspot => ({
          ...hotspot,
          demand_score: parseFloat(hotspot.demand_score),
          // Add recommendation logic based on demand score
          recommendation: {
            action: hotspot.demand_score >= 4.0 ? 'GO_NOW' : 
                   hotspot.demand_score >= 3.0 ? 'WAIT' : 'AVOID',
            message: hotspot.demand_score >= 4.0 ? 
              'Hohe Nachfrage - Jetzt hinfahren!' :
              hotspot.demand_score >= 3.0 ?
              'Moderate Nachfrage - Abwarten oder hinfahren' :
              'Niedrige Nachfrage - Andere Gebiete bevorzugen'
          },
          // Calculate distance if user location is available
          distance: userLocation ? 
            calculateDistance(
              userLocation.lat, userLocation.lng, 
              parseFloat(hotspot.location_lat), parseFloat(hotspot.location_lng)
            ).toFixed(1) : null
        }));

        setHotspots(processedHotspots);
        setError(null);
      }
    } catch (err) {
      setError('Fehler beim Laden der Hotspots');
      console.error('Error fetching hotspots:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log('Geolocation error:', error);
        }
      );
    }
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getRecommendationColor = (action) => {
    switch (action) {
      case 'GO_NOW': return 'bg-green-500 text-white';
      case 'WAIT': return 'bg-yellow-500 text-white';
      case 'AVOID': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getRecommendationIcon = (action) => {
    switch (action) {
      case 'GO_NOW': return <Navigation size={16} />;
      case 'WAIT': return <Clock size={16} />;
      case 'AVOID': return <Car size={16} className="rotate-180" />;
      default: return <MapPin size={16} />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'EVENT_BASED': return '🎉';
      case 'TIME_BASED': return '⏰';
      case 'WEATHER_BASED': return '🌤️';
      default: return '📍';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'EVENT_BASED': return 'Event-basiert';
      case 'TIME_BASED': return 'Zeit-basiert';
      case 'WEATHER_BASED': return 'Wetter-basiert';
      default: return 'Allgemein';
    }
  };

  const openInMaps = (lat, lng, name) => {
    const url = `https://maps.google.com/maps?daddr=${lat},${lng}&saddr=${userLocation?.lat || ''},${userLocation?.lng || ''}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-100 h-32 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          <TrendingUp className="mr-2 text-green-600" size={24} />
          Hotspot Empfehlungen
        </h2>
        <button
          onClick={fetchHotspots}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Aktualisieren
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {hotspots.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <TrendingUp size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-lg">Keine Hotspots verfügbar</p>
          <p className="text-sm">Empfehlungen werden basierend auf aktueller Nachfrage angezeigt</p>
        </div>
      ) : (
        <div className="space-y-4">
          {hotspots.map((hotspot) => {
            const distance = userLocation ? 
              calculateDistance(
                userLocation.lat, 
                userLocation.lng, 
                parseFloat(hotspot.location_lat), 
                parseFloat(hotspot.location_lng)
              ) : null;

            return (
              <div key={hotspot.id} className="border rounded-lg p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-2xl">{getTypeIcon(hotspot.recommendation_type)}</span>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {hotspot.name}
                      </h3>
                      <span className="text-sm px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                        {getTypeText(hotspot.recommendation_type)}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-2">{hotspot.area_name}</p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center">
                        <Star className="text-yellow-500 mr-1" size={16} />
                        {hotspot.demand_score}/5.0
                      </div>
                      
                      {distance && (
                        <div className="flex items-center">
                          <MapPin className="text-blue-500 mr-1" size={16} />
                          {distance.toFixed(1)} km entfernt
                        </div>
                      )}
                      
                      {hotspot.isActiveNow && (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                          Jetzt aktiv
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className={`px-3 py-2 rounded-lg flex items-center space-x-2 ${getRecommendationColor(hotspot.recommendation.action)}`}>
                    {getRecommendationIcon(hotspot.recommendation.action)}
                    <span className="font-medium text-sm">
                      {hotspot.recommendation.priority}
                    </span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-gray-800 mb-2">📋 Beschreibung</h4>
                  <p className="text-gray-700 mb-3">{hotspot.description}</p>
                  
                  {hotspot.tips && (
                    <>
                      <h4 className="font-medium text-gray-800 mb-2">💡 Tipps</h4>
                      <p className="text-gray-700">{hotspot.tips}</p>
                    </>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Clock className="mr-1" size={16} />
                      {hotspot.active_hours_start} - {hotspot.active_hours_end}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openInMaps(hotspot.location_lat, hotspot.location_lng, hotspot.name)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    >
                      <Navigation size={16} className="mr-2" />
                      Navigation
                    </button>
                  </div>
                </div>

                {hotspot.recommendation && (
                  <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                    <p className="text-blue-800 font-medium">
                      🎯 {hotspot.recommendation.message}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg">
        <h4 className="font-medium text-blue-800 mb-2">🎯 Hotspot System</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Event-basiert:</strong> Konzerte, Spiele, besondere Veranstaltungen</li>
          <li>• <strong>Zeit-basiert:</strong> Rush Hour, Feierabend, Flugzeiten</li>
          <li>• <strong>Wetter-basiert:</strong> Regen, Schnee, extreme Temperaturen</li>
          <li>• Empfehlungen werden alle 5 Minuten aktualisiert</li>
        </ul>
      </div>
    </div>
  );
};

export default HotspotRecommendations;
